import { useState } from 'react';
import { useQueue, useReviewAction, type QueueItem, type QueueFilters } from '../hooks/useApi';

const CATEGORY_COLORS: Record<string, string> = {
  CUSTOM_ORDER: 'bg-blue-100 text-blue-800',
  PROD_STATUS: 'bg-green-100 text-green-800',
  SHIPPING: 'bg-yellow-100 text-yellow-800',
  PRODUCT_SUPPORT: 'bg-teal-100 text-teal-800',
  WHOLESALE: 'bg-purple-100 text-purple-800',
  FEEDBACK: 'bg-orange-100 text-orange-800',
  BOOKING: 'bg-cyan-100 text-cyan-800',
  INTERNAL: 'bg-gray-100 text-gray-700',
};

const CATEGORIES = Object.keys(CATEGORY_COLORS);

function categoryBadgeClass(category: string): string {
  return CATEGORY_COLORS[category] ?? 'bg-gray-100 text-gray-700';
}

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function confidenceLabel(c: number): string {
  if (c >= 0.9) return 'High';
  if (c >= 0.5) return 'Medium';
  return 'Low';
}

function priorityGroup(item: QueueItem): 'review' | 'medium' | 'high' {
  if (item.escalation_flag || item.confidence < 0.5) return 'review';
  if (item.confidence < 0.9) return 'medium';
  return 'high';
}

const PRIORITY_CONFIG = {
  review: { label: 'NEEDS REVIEW', border: 'border-l-red-500', bg: 'bg-red-50', text: 'text-red-700' },
  medium: { label: 'MEDIUM CONFIDENCE', border: 'border-l-amber-500', bg: 'bg-amber-50', text: 'text-amber-700' },
  high: { label: 'HIGH CONFIDENCE', border: 'border-l-emerald-500', bg: 'bg-emerald-50', text: 'text-emerald-700' },
};

function QueueCard({ item }: { item: QueueItem }) {
  const [expanded, setExpanded] = useState(false);
  const reviewAction = useReviewAction();
  const group = priorityGroup(item);
  const config = PRIORITY_CONFIG[group];

  const handleAction = (action: 'approve' | 'reject') => {
    reviewAction.mutate({ id: item.id, action });
  };

  return (
    <div className={`card border-l-4 ${config.border}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-semibold text-gray-900 truncate">
              {item.from_name || item.from_email}
            </span>
            {item.from_name && (
              <span className="text-xs text-gray-500 truncate">&lt;{item.from_email}&gt;</span>
            )}
          </div>
          <p className="mt-0.5 text-sm font-medium text-gray-800 truncate">{item.subject}</p>
          <p className="mt-1 text-xs text-gray-500 line-clamp-2">{item.body_preview}</p>
        </div>

        <div className="flex flex-shrink-0 flex-col items-end gap-1.5">
          <span className={`badge ${categoryBadgeClass(item.category)}`}>{item.category.replace('_', ' ')}</span>
          <span
            className={`text-xs font-medium ${
              item.confidence >= 0.9
                ? 'text-emerald-600'
                : item.confidence >= 0.5
                  ? 'text-amber-600'
                  : 'text-red-600'
            }`}
          >
            {(item.confidence * 100).toFixed(0)}% {confidenceLabel(item.confidence)}
          </span>
          <span className="text-xs text-gray-400">{timeAgo(item.received_at)}</span>
        </div>
      </div>

      {item.escalation_flag && item.escalation_reason && (
        <div className="mt-2 rounded bg-red-50 px-3 py-1.5 text-xs text-red-700">
          Escalation: {item.escalation_reason}
        </div>
      )}

      {/* Actions */}
      <div className="mt-3 flex items-center gap-2">
        <button
          onClick={() => setExpanded((e) => !e)}
          className="btn-outline text-xs"
        >
          {expanded ? 'Hide Draft' : 'View Draft'}
        </button>
        <button
          onClick={() => handleAction('approve')}
          disabled={reviewAction.isPending}
          className="btn-success text-xs"
        >
          Approve
        </button>
        <button
          onClick={() => handleAction('reject')}
          disabled={reviewAction.isPending}
          className="btn-danger text-xs"
        >
          Reject
        </button>
      </div>

      {/* Expandable draft */}
      {expanded && (
        <div className="mt-3 rounded border border-gray-200 bg-gray-50 p-3">
          <p className="mb-1 text-xs font-medium text-gray-500 uppercase tracking-wide">AI Draft Response</p>
          <div className="whitespace-pre-wrap text-sm text-gray-700">{item.draft_response}</div>
        </div>
      )}
    </div>
  );
}

export default function QueueView() {
  const [filters, setFilters] = useState<QueueFilters>({});
  const { data: items, isLoading, isError } = useQueue(filters);

  const grouped = {
    review: [] as QueueItem[],
    medium: [] as QueueItem[],
    high: [] as QueueItem[],
  };

  items?.forEach((item) => {
    grouped[priorityGroup(item)].push(item);
  });

  return (
    <div>
      {/* Filters */}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <select
          className="select w-44"
          value={filters.category ?? ''}
          onChange={(e) => setFilters((f) => ({ ...f, category: e.target.value || undefined }))}
        >
          <option value="">All Categories</option>
          {CATEGORIES.map((c) => (
            <option key={c} value={c}>
              {c.replace('_', ' ')}
            </option>
          ))}
        </select>

        <select
          className="select w-48"
          value={
            filters.max_confidence !== undefined
              ? `${filters.min_confidence ?? 0}-${filters.max_confidence}`
              : ''
          }
          onChange={(e) => {
            if (!e.target.value) {
              setFilters((f) => ({ ...f, min_confidence: undefined, max_confidence: undefined }));
            } else {
              const [min, max] = e.target.value.split('-').map(Number);
              setFilters((f) => ({ ...f, min_confidence: min, max_confidence: max }));
            }
          }}
        >
          <option value="">All Confidence</option>
          <option value="0-0.49">Needs Review (&lt;50%)</option>
          <option value="0.5-0.89">Medium (50-89%)</option>
          <option value="0.9-1">High (90%+)</option>
        </select>

        <span className="ml-auto text-sm text-gray-500">
          {items?.length ?? 0} pending
        </span>
      </div>

      {isLoading && (
        <div className="flex justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-600 border-t-transparent" />
        </div>
      )}

      {isError && (
        <div className="card border-red-200 bg-red-50 text-center text-red-700">
          Failed to load queue. Check your connection and API key.
        </div>
      )}

      {!isLoading && !isError && items?.length === 0 && (
        <div className="card text-center text-gray-500 py-12">
          No emails pending review. Nice work!
        </div>
      )}

      {/* Priority groups */}
      {(['review', 'medium', 'high'] as const).map((group) => {
        const groupItems = grouped[group];
        if (groupItems.length === 0) return null;
        const config = PRIORITY_CONFIG[group];
        return (
          <div key={group} className="mb-6">
            <div className={`mb-2 inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wide ${config.bg} ${config.text}`}>
              <span className={`h-2 w-2 rounded-full ${config.border.replace('border-l-', 'bg-')}`} />
              {config.label} ({groupItems.length})
            </div>
            <div className="flex flex-col gap-3">
              {groupItems.map((item) => (
                <QueueCard key={item.id} item={item} />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
